import React, { useRef, useState, useCallback, useEffect, createContext } from "react";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";

export const ScrollContext = createContext({ springY: null });

const SmoothScroll = ({ children }) => {
  const contentRef = useRef(null);
  const [pageHeight, setPageHeight] = useState(0);

  // Measure the height of the content
  const resizePageHeight = useCallback((entries) => {
    for (let entry of entries) {
      setPageHeight(entry.contentRect.height);
    }
  }, []);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => resizePageHeight(entries));
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }
    return () => resizeObserver.disconnect();
  }, [resizePageHeight]);

  const { scrollY } = useScroll();

  // Smooth out the native scroll
  const springY = useSpring(scrollY, {
    stiffness: 100,
    damping: 20,
    mass: 0.5,
  });

  const y = useTransform(springY, (value) => `-${value}px`);

  // Intercept the wheel event to make scrolling faster
  useEffect(() => {
    const handleWheel = (e) => {
      // Allow pinch-to-zoom and horizontal scrolls to bypass this
      if (e.ctrlKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      
      e.preventDefault();
      
      const speedMultiplier = 1.8; // Fast scroll
      window.scrollBy({
        top: e.deltaY * speedMultiplier,
        behavior: 'auto'
      });
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => window.removeEventListener('wheel', handleWheel);
  }, []);

  return (
    <ScrollContext.Provider value={{ springY }}>
      <motion.div
        ref={contentRef}
        style={{ y }}
        className="fixed top-20 left-0 right-0 w-full will-change-transform z-10"
      >
        {children}
      </motion.div>
      {/* Ghost div to expand the scrollable area */}
      <div style={{ height: pageHeight }} />
    </ScrollContext.Provider>
  );
};

export default SmoothScroll;
